/**
 * **打印包的样式** —— 用一个 `<style>` 标签注入，类名统一 `npp-` 前缀。
 *
 * ## 为什么不是 `.vue` 里的 `<style>` / 一个 `.css` 文件
 *
 * 这个包是**代码包**：它的组件以 `NodePackage.editors[].component` 的形式
 * 进注册表，而装配点 `playground/nodeTypes.ts` 会被**一堆 esbuild 脚本**
 * 打包（`verify:packages` / `verify:boundary` / `verify:layers` …）——
 * 那条路径上**没有 SFC 编译器、也没有 CSS loader**（和夹具包必须写成
 * `.ts` 是同一个原因，见 `scripts/fixtures/editor-package.ts` 的文件头）。
 *
 * 所以样式只能是"运行时可注入的字符串"，在**挂载时**注入一次
 * （不是 import 时 —— 那些脚本里根本没有 `document`）。
 *
 * ## 两条纪律
 *
 * ① 每个选择器都挂在 `.npp` / `.npp-p` 这两个根下面：
 *    包没有资格给别人的 DOM 上样式（`node-packages.md` §4 的 B 路就是这么被否掉的）；
 * ② 颜色一律走**主题变量**（带兜底值）：主题是宿主的能力，
 *    包写死颜色就会在浅色主题下变成一块黑。
 */
import { PRINT_PANEL_CLASS, PRINT_EDITOR_CLASS } from './classNames'

/** `<style>` 的 id（幂等注入用） */
export const PRINT_STYLE_ID = 'np-print-editor-styles'

const CSS = `
/* ── 编辑器主体 ─────────────────────────────────────────────── */
.${PRINT_EDITOR_CLASS} {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--np-surface-0, #1b1b1b);
  color: var(--np-panel-text, #e2e2e2);
  font-size: 10.5px;
}
/*
 * 二级行（工具栏）：**可横滚**，窄窗口下不许被压扁变形。
 *
 * ⚠️ 这套规则和内核的 .np-strip（theme/np-strip.css）是**同一条**，
 * 但包不能假设宿主 import 了它 —— 包自己的样式块必须自足，
 * 所以这里再写一份（两处的注释互相点名，改一处要改两处）。
 */
.${PRINT_EDITOR_CLASS}__bar {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 4px 8px;
  border-bottom: 1px solid var(--np-border-soft, rgba(255,255,255,0.1));
  background: var(--np-surface-bar-soft, transparent);
  /*
   * ⚠️ **不换行 + 横滚**（原来是 flex-wrap: wrap）：
   * 换行会让工具条在窄窗口里变成两行、把画布往下挤（"变形"）；
   * 横滚则是"都在，只是要划一下"（真机反馈："像页面编辑器的二级行，
   * 应该也是可以滚动的，防止宽度不够的时候被变形"）。
   */
  flex-wrap: nowrap;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  touch-action: pan-x;
}
.${PRINT_EDITOR_CLASS}__bar::-webkit-scrollbar { display: none; }
/* 条里的按钮一律不收缩（收缩就是被压扁） */
.${PRINT_EDITOR_CLASS}__bar > * { flex: 0 0 auto; }
.${PRINT_EDITOR_CLASS}__spacer { flex: 1 1 auto; }
.${PRINT_EDITOR_CLASS}__zoom {
  min-width: 46px;
  text-align: center;
  color: var(--np-panel-dim, #9a9a9a);
  font-variant-numeric: tabular-nums;
}
.${PRINT_EDITOR_CLASS}__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 14px;
}
.${PRINT_EDITOR_CLASS}__pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
  /*
   * ⚠️（注意：本文件的注释里**不能出现反引号** —— 整段 CSS 是一个模板字符串，
   * 反引号会把它提前截断。这是这个文件唯一的坑。）
   *
   * width: max-content + min-width: 100% 是**修 bug 的**，不是排版偏好：
   * 滚动容器里用 flex 居中，内容比容器宽时**左边那一截滚不到**
   * （flex 的居中溢出会同时往两边顶，而滚动只能到 0）——
   * 真机反馈的"放大之后超出的左边部分看不到"就是它。
   * 让这层自己撑到内容宽度，滚动范围就覆盖了整张纸。
   */
  width: max-content;
  min-width: 100%;
}

/* ── 一张纸 ─────────────────────────────────────────────────── */
.${PRINT_EDITOR_CLASS}__sheet { position: relative; }
.${PRINT_EDITOR_CLASS}__ruler-x,
.${PRINT_EDITOR_CLASS}__ruler-y {
  position: absolute;
  color: var(--np-panel-dim, #9a9a9a);
  font-size: 8px;
  line-height: 1;
  pointer-events: none;
  font-variant-numeric: tabular-nums;
}
.${PRINT_EDITOR_CLASS}__ruler-x { left: 14px; top: 0; height: 12px; right: 0; }
.${PRINT_EDITOR_CLASS}__ruler-y { top: 12px; left: 0; width: 12px; bottom: 0; }
.${PRINT_EDITOR_CLASS}__tick { position: absolute; }
.${PRINT_EDITOR_CLASS}__ruler-x .${PRINT_EDITOR_CLASS}__tick { top: 2px; }
.${PRINT_EDITOR_CLASS}__ruler-y .${PRINT_EDITOR_CLASS}__tick { left: 2px; }
.${PRINT_EDITOR_CLASS}__page {
  position: absolute;
  left: 14px;
  top: 12px;
  background: var(--np-surface-2, #262626);
  border: 1px solid var(--np-border-strong, rgba(255,255,255,0.22));
  box-shadow: var(--np-shadow-float, 0 6px 20px rgba(0,0,0,0.35));
  overflow: hidden;
  /*
   * ⚠️ 纸面上**不禁用**触摸手势（元素与把手才禁用）：手机上纸往往比屏幕高，
   * 而拖纸面本身没有任何行为 —— 拦掉原生滚动等于"滚不动，也拖不动"。
   */
  touch-action: pan-x pan-y;
}
.${PRINT_EDITOR_CLASS}__grid {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.${PRINT_EDITOR_CLASS}__margin {
  position: absolute;
  border: 1px dashed var(--np-border-soft, rgba(255,255,255,0.16));
  pointer-events: none;
}
.${PRINT_EDITOR_CLASS}__label {
  /*
   * ⚠️ 绝对定位（不是流式）：纸是 absolute，不在流里 —— 留在流里会被纸盖住。
   * 位置由组件按"纸高 + 标尺高"算出来（见 renderSheet）。
   */
  position: absolute;
  left: 14px;
  right: 0;
  text-align: center;
  color: var(--np-panel-dim, #9a9a9a);
  font-variant-numeric: tabular-nums;
}

/* ── 元素 ───────────────────────────────────────────────────── */
.${PRINT_EDITOR_CLASS}__el {
  position: absolute;
  box-sizing: border-box;
  cursor: move;
  color: var(--np-panel-text, #e2e2e2);
  overflow: hidden;
  white-space: pre-wrap;
  touch-action: none;
  user-select: none;
}
.${PRINT_EDITOR_CLASS}__el[data-kind='text'] {
  display: flex;
  align-items: flex-start;
  line-height: 1.15;
}
.${PRINT_EDITOR_CLASS}__el[data-selected='true'] {
  outline: 1px solid var(--np-accent, #f0a04b);
  outline-offset: 0;
}
.${PRINT_EDITOR_CLASS}__box {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
/*
 * ── 文本的"框 / 底"两层 ────────────────────────────────────────
 *
 * ⚠️★ 这两条类是**漏掉过**的：组件里写着 cls('text-frame')，样式表里没有它 ——
 * 而漏掉的症状不是"样式差一点"，是**结构性的**：
 * 一个没有定位的空 span 在 .el（display: flex，见上）里就是一个
 * **flex 子项**，于是那个"边框层"变成文字左边一个 2×2 px 的黑方块，
 * 文字被挤到它右边排 —— 用户看到的就是"一个黑块和文本排列，
 * 而不是节点区域的边框"（真机反馈）。
 *
 * 所以这三层（矩形的 __box、文本的 __text-frame / __invert）是同一种东西：
 * **铺满组件框的覆盖层，不参与排版**。三条规则写在一起，就是为了
 * 下次加一层时一眼看见"要定位"。
 *
 * ⚠️ 包自己的类名没有死类检查（classNames.ts 说明了为什么：内核的
 * verify:css 只管 np- / pg- 那两套），所以"写了类、忘了样式"
 * 在这边没人拦 —— verify:print-dom 里有一条断言盯着这三层的计算样式。
 */
.${PRINT_EDITOR_CLASS}__text-frame,
.${PRINT_EDITOR_CLASS}__invert {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
/*
 * ── 框 / 底画在内容**之下**（和 IR 的指令顺序同一条）───────────────
 *
 * ⚠️★ 这不是排版偏好，是**两个视图对不对得上**的问题：IR 里
 * pushFrameOp() 先把框 / 底画成一条 rect op、内容 op 在后面 ——
 * 所以预览里"填充"永远盖不住文字 / 图片。而 CSS 的绘制顺序恰好相反：
 * **定位元素画在流内内容之上**（.npp__text-frame 比 .npp__text 后画），
 * 于是编辑视图里一开填充，文字就被那块底色盖住了（预览里没有这回事）——
 * 又是一处"编辑视图 ≠ 预览"。
 *
 * 所以框 / 底这三层用 z-index: -1；而 .npp__ink 必须**自成层叠上下文**
 * （z-index: 0）：负 z-index 只会落到本层叠上下文的流内内容之下 ——
 * 不这么做的话它会跑到纸（.npp__page）的背景下面，边框直接看不见。
 */
.${PRINT_EDITOR_CLASS}__invert,
.${PRINT_EDITOR_CLASS}__box,
.${PRINT_EDITOR_CLASS}__text-frame {
  z-index: -1;
}
/* ── 条码 / 二维码 ─────────────────────────────────────────── */
.${PRINT_EDITOR_CLASS}__barcode {
  position: absolute;
  inset: 0;
  /*
   * ⚠️★ 这里**故意不写死颜色**（原来是 var(--np-panel-text, #e2e2e2)）。
   *
   * 条与模块的 fill 是 currentColor，而"墨色"是**参数**（color，
   * 默认 #000000）—— 它写在 .el 的内联样式上（见 PageEditor.renderElement）。
   * color 是可继承属性，但**元素自己命中的规则会盖掉继承值**：
   * 于是这条例把参数里的墨色顶掉了 ——
   * 编辑视图里条码是灰白的（面板文字色），预览里才是黑的（预览走 IR 的 op.color）。
   *
   * 真机反馈："条码和二维码在编辑模式下颜色是灰白色，预览模式才是黑色"。
   * 现在这一层用 inherit（= .el 上那个墨色参数）：编辑视图画的就是**参数里那个颜色**
   * （和 IR 的 op.color 同源；预览再把它二值化成"上墨 / 不上墨"），
   * 换颜色参数（多色带分版要用的那个）时两处一起变。
   */
  color: inherit;
}
.${PRINT_EDITOR_CLASS}__barcode-svg { display: block; pointer-events: none; }
.${PRINT_EDITOR_CLASS}__barcode-frame {
  position: absolute;
  top: 0;
  border: 1px dashed var(--np-border-soft, rgba(255,255,255,0.18));
  pointer-events: none;
}
.${PRINT_EDITOR_CLASS}__barcode-hri {
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  overflow: hidden;
  font-variant-numeric: tabular-nums;
}
.${PRINT_EDITOR_CLASS}__barcode-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  box-sizing: border-box;
  border: 1px dashed var(--np-danger, #e06c75);
  color: var(--np-danger, #e06c75);
  font-size: 9px;
  line-height: 1.2;
  text-align: center;
  overflow: hidden;
}
.${PRINT_EDITOR_CLASS}__handle {
  position: absolute;
  width: 7px;
  height: 7px;
  margin: -4px 0 0 -4px;
  background: var(--np-accent-solid, #f0a04b);
  border: 1px solid var(--np-surface-0, #1b1b1b);
  border-radius: 1px;
  touch-action: none;
}
.${PRINT_EDITOR_CLASS}__guide {
  position: absolute;
  background: var(--np-accent, #f0a04b);
  pointer-events: none;
  opacity: 0.9;
}
.${PRINT_EDITOR_CLASS}__guide--x { top: 0; bottom: 0; width: 1px; }
.${PRINT_EDITOR_CLASS}__guide--y { left: 0; right: 0; height: 1px; }

/* ── 空状态（模板里还没有页面节点）──────────────────────────── */
.${PRINT_EDITOR_CLASS}__empty {
  margin: auto;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 18px;
  text-align: center;
  color: var(--np-panel-dim, #9a9a9a);
  border: 1px dashed var(--np-border-soft, rgba(255,255,255,0.14));
  border-radius: var(--np-radius-m, 6px);
}
.${PRINT_EDITOR_CLASS}__empty strong { color: var(--np-panel-text, #e2e2e2); font-size: 12px; }

/* ── 按钮与控件（编辑器和面板共用）─────────────────────────── */
.npp-btn {
  height: var(--np-ctl-h-sm, 22px);
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--np-border-soft, rgba(255,255,255,0.12));
  border-radius: var(--np-radius-s, 4px);
  background: var(--np-widget-bg, rgba(255,255,255,0.04));
  color: inherit;
  font: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.npp-btn:hover { background: var(--np-widget-bg-hover, rgba(255,255,255,0.09)); }
.npp-btn[data-active='true'] {
  border-color: var(--np-accent, #f0a04b);
  color: var(--np-accent, #f0a04b);
}
.npp-btn--accent {
  border-color: var(--np-accent, #f0a04b);
  background: var(--np-accent-soft, rgba(240,160,75,0.16));
}
.npp-btn--danger:hover { border-color: var(--np-danger, #e06c75); color: var(--np-danger, #e06c75); }
.npp-field { display: flex; align-items: center; gap: 6px; min-height: var(--np-ctl-h-sm, 22px); }
.npp-field__label { flex: 0 0 46px; color: var(--np-panel-dim, #9a9a9a); }
.npp-field__gap { flex: 1 1 auto; }
.npp-input {
  flex: 1 1 auto;
  min-width: 0;
  height: var(--np-ctl-h-sm, 22px);
  box-sizing: border-box;
  padding: 0 6px;
  border: 1px solid var(--np-border-soft, rgba(255,255,255,0.12));
  border-radius: var(--np-radius-s, 4px);
  background: var(--np-panel-input-bg, rgba(255,255,255,0.04));
  color: inherit;
  font: inherit;
  font-variant-numeric: tabular-nums;
}
.npp-input--area { height: 46px; padding: 4px 6px; resize: vertical; }
.npp-sep { width: 1px; height: 16px; background: var(--np-border-soft, rgba(255,255,255,0.14)); }

/* ── 精确预览（IR 画出来的那一档）─────────────────────────── */
/*
 * ⚠️ 结构：.npp__preview（这一层）里第一件是 **.npp__sheet**（标尺 + 纸 + 标签，
 * 和编辑视图同一个组件函数），问题清单在它**下面**。
 * 所以这一层是 flex 列 —— 壳自己有宽高（在流里），问题清单自然排在它下方。
 */
.${PRINT_EDITOR_CLASS}__preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
/*
 * ⚠️ 预览的纸**和编辑视图的纸同一个位置**（left: 14px; top: 12px）：
 * 标尺是绝对定位的，纸不摆在标尺交角上的话，尺子的 0 就不在纸的边上。
 * 纸不在流里（position: absolute），所以位置只由这两行决定 ——
 * 改这里必须同时改 .npp__page 那一条。
 */
.${PRINT_EDITOR_CLASS}__preview-svg {
  position: absolute;
  left: 14px;
  top: 12px;
  display: block;
  box-shadow: var(--np-shadow-float, 0 6px 20px rgba(0,0,0,0.35));
}
.${PRINT_EDITOR_CLASS}__preview-problem {
  max-width: 420px;
  padding: 3px 6px;
  border-radius: var(--np-radius-s, 4px);
  font-size: 10px;
  line-height: 1.3;
}
.${PRINT_EDITOR_CLASS}__preview-problem.is-error {
  border: 1px solid var(--np-danger, #e06c75);
  color: var(--np-danger, #e06c75);
}
.${PRINT_EDITOR_CLASS}__preview-problem.is-warning {
  border: 1px solid var(--np-severity-warning, #d8a657);
  color: var(--np-severity-warning, #d8a657);
}

/* ── 问题横幅（编辑视图也显示）─────────────────────────────── */
.${PRINT_EDITOR_CLASS}__problems {
  /*
   * ⚠️ **浮层**，不占布局（真机反馈："它的显示和隐藏会导致画布区域移动"）。
   * position: absolute + pointer-events: none（除了里面的按钮）——
   * 出现和消失都不影响纸的位置，也就不会在操作中途把画布顶下去。
   */
  position: absolute;
  left: 8px;
  bottom: 8px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 60%;
  padding: 4px 8px;
  border-radius: var(--np-radius-s, 4px);
  border: 1px solid var(--np-border-soft, rgba(255,255,255,0.15));
  background: var(--np-surface-float, rgba(20,20,20,0.92));
  box-shadow: var(--np-shadow-float, 0 6px 20px rgba(0,0,0,0.35));
  font-size: 10px;
  pointer-events: none;
}
.${PRINT_EDITOR_CLASS}__problem-list {
  position: absolute;
  left: 8px;
  bottom: 32px;
  z-index: 5;
  /*
   * ⚠️ 高度：真机反馈"详情高度太窄了，看不见"。25vh 的下限是"至少能看四五条"，
   * 上限 60% 是"别把整张纸盖住"。滚动条留给内容超长时用。
   */
  min-height: 25vh;
  max-height: 60vh;
  width: min(420px, 60%);
  overflow: auto;
  padding: 6px 8px;
  border-radius: var(--np-radius-s, 4px);
  border: 1px solid var(--np-border-soft, rgba(255,255,255,0.15));
  background: var(--np-surface-float, rgba(20,20,20,0.96));
  box-shadow: var(--np-shadow-float, 0 6px 20px rgba(0,0,0,0.35));
  font-size: 10.5px;
  line-height: 1.5;
}

/* 感叹号徽标（红 = 错误 / 黄 = 提醒 / 灰 = 信息） */
.${PRINT_EDITOR_CLASS}__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  pointer-events: auto;
  cursor: pointer;
  border: 0;
  border-radius: 3px;
  padding: 1px 6px;
  font: inherit;
  color: var(--np-text, #e8e8e8);
  background: transparent;
}
.${PRINT_EDITOR_CLASS}__badge:hover { background: var(--np-surface-hover, rgba(255,255,255,0.08)); }
.${PRINT_EDITOR_CLASS}__badge--error { color: var(--np-danger, #e5534b); }
.${PRINT_EDITOR_CLASS}__badge--warning { color: var(--np-warn, #d9a23b); }
.${PRINT_EDITOR_CLASS}__badge--info { color: var(--np-muted, #9a9a9a); }
.${PRINT_EDITOR_CLASS}__problem-item { padding: 2px 0; }
/*
 * 圆角裁剪层：**只有它裁**（overflow: hidden + 半径）。
 * ⚠️ 把手不在这一层里 —— 它们骑在框边上，被裁掉就没法拖了（见 renderElement）。
 */
/* 滤镜定义用的隐藏 svg（不占位置、不可见） */
.${PRINT_EDITOR_CLASS}__defs {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  pointer-events: none;
}
.${PRINT_EDITOR_CLASS}__clip {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}
.${PRINT_EDITOR_CLASS}__ink {
  position: absolute;
  inset: 0;
  display: block;
  /* 旋转绕中心（和 IR 的 pivotDots = 框中心 是同一条约定） */
  transform-origin: center;
  /*
   * ★ **自成层叠上下文**（见上面"框 / 底画在内容之下"那段）：
   * 框 / 底那三层是 z-index: -1，只有在**本层**里才安全 ——
   * 没有这一行，负 z-index 会继续往上找层叠上下文，落到纸（.npp__page）
   * 的背景之下，边框与底色就整个看不见了。
   */
  z-index: 0;
}
.npp-input--color {
  width: 28px;
  height: 20px;
  padding: 0;
  border: 1px solid var(--np-border-soft, rgba(255,255,255,0.15));
}
.${PRINT_EDITOR_CLASS}__problem-error { color: var(--np-danger, #e06c75); }
.${PRINT_EDITOR_CLASS}__problem-warning { color: var(--np-severity-warning, #d8a657); }
.${PRINT_EDITOR_CLASS}__problem-note { color: var(--np-panel-dim, #9a9a9a); }

/* ── 侧栏面板的卡片（可折叠分组）───────────────────────────── */
.${PRINT_PANEL_CLASS}__group {
  border: 1px solid var(--np-border-soft, rgba(255,255,255,0.08));
  border-radius: var(--np-radius-s, 4px);
  margin-bottom: 4px;
  overflow: hidden;
}
.${PRINT_PANEL_CLASS}__title {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  cursor: pointer;
  user-select: none;
  background: var(--np-surface-bar-soft, rgba(255,255,255,0.03));
}
.${PRINT_PANEL_CLASS}__group.is-open .${PRINT_PANEL_CLASS}__title {
  border-bottom: 1px solid var(--np-border-soft, rgba(255,255,255,0.08));
}
.${PRINT_PANEL_CLASS}__group:not(.is-open) .${PRINT_PANEL_CLASS}__title {
  border-bottom: none;
}
.${PRINT_PANEL_CLASS}__caret {
  width: 8px;
  color: var(--np-panel-dim, #9a9a9a);
}
.${PRINT_PANEL_CLASS}__group > .npp-field,
.${PRINT_PANEL_CLASS}__group > .${PRINT_PANEL_CLASS}__group,
.${PRINT_PANEL_CLASS}__group > .${PRINT_PANEL_CLASS}__row {
  margin: 4px 6px;
}

/* ── 侧栏面板 ───────────────────────────────────────────────── */
.${PRINT_PANEL_CLASS} {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  color: var(--np-panel-text, #e2e2e2);
  font-size: 10.5px;
  overflow: auto;
}
.${PRINT_PANEL_CLASS}__title {
  color: var(--np-panel-dim, #9a9a9a);
  text-transform: none;
  letter-spacing: 0.02em;
}
.${PRINT_PANEL_CLASS}__group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 6px;
  border-top: 1px solid var(--np-border-soft, rgba(255,255,255,0.08));
}
.${PRINT_PANEL_CLASS}__row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 4px;
  border-radius: var(--np-radius-s, 4px);
  cursor: pointer;
}
.${PRINT_PANEL_CLASS}__row:hover { background: var(--np-panel-bg-hover, rgba(255,255,255,0.06)); }
.${PRINT_PANEL_CLASS}__row[data-selected='true'] {
  background: var(--np-accent-soft, rgba(240,160,75,0.16));
  color: var(--np-accent, #f0a04b);
}
.${PRINT_PANEL_CLASS}__muted { color: var(--np-panel-dim, #9a9a9a); }
.${PRINT_PANEL_CLASS}__empty { color: var(--np-panel-mute, #7a7a7a); font-style: italic; }
`

/**
 * 注入样式（幂等）。
 *
 * ⚠️ 只在**挂载时**调 —— 这个模块也会被 esbuild 打包进 node 脚本
 * （那些进程里 `document` 是 undefined），import 时就注入会当场炸。
 */
export function ensurePrintStyles(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(PRINT_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = PRINT_STYLE_ID
  style.textContent = CSS
  document.head.appendChild(style)
}
